/**
 * Client HTTP centralise pour l'API Mopozzi.
 * Injection automatique du token Bearer, rafraichissement automatique (401),
 * et detection des cles d'API passerelle manquantes (403).
 */

export interface RequestOptions extends RequestInit {
  params?: Record<string, any>;
  skipAuth?: boolean;
}

export class ApiError extends Error {
  status: number;
  statusText: string;
  data: any;

  constructor(status: number, statusText: string, message: string, data?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.statusText = statusText;
    this.data = data;
  }
}

class HttpClient {
  private isRefreshing = false;
  private refreshSubscribers: ((token: string) => void)[] = [];

  private getBaseUrl(): string {
    const envUrl = process.env.REACT_APP_MOPOZZI_API_BASE_URL || process.env.REACT_APP_DIGIASSUR_BASE_URL;
    if (envUrl) {
      return envUrl.endsWith("/api") ? envUrl : `${envUrl}/api`;
    }
    return "http://localhost:8000/api";
  }

  private getToken(): string | null {
    if (typeof window === "undefined") return null;
    return sessionStorage.getItem("token");
  }

  private getRefreshToken(): string | null {
    if (typeof window === "undefined") return null;
    return sessionStorage.getItem("refresh_token");
  }

  private setToken(token: string, refreshToken?: string): void {
    if (typeof window === "undefined") return;
    sessionStorage.setItem("token", token);
    if (refreshToken) {
      sessionStorage.setItem("refresh_token", refreshToken);
    }
  }

  private onTokenRefreshed(token: string) {
    this.refreshSubscribers.forEach((cb) => cb(token));
    this.refreshSubscribers = [];
  }

  private addRefreshSubscriber(cb: (token: string) => void) {
    this.refreshSubscribers.push(cb);
  }

  private async tryRefreshToken(): Promise<string | null> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return null;

    try {
      const response = await fetch(`${this.getBaseUrl()}/token/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const newToken = data.token;
      if (newToken) {
        this.setToken(newToken, data.refresh_token);
        return newToken;
      }
      return null;
    } catch {
      return null;
    }
  }

  private buildUrl(path: string, params?: Record<string, any>): string {
    const normalizedPath = path.startsWith("http")
      ? path
      : `${this.getBaseUrl()}${path.startsWith("/") ? path : `/${path}`}`;

    if (!params) return normalizedPath;

    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        query.append(key, String(value));
      }
    });

    const queryString = query.toString();
    if (!queryString) return normalizedPath;

    return `${normalizedPath}${normalizedPath.includes("?") ? "&" : "?"}${queryString}`;
  }

  public async request<T = any>(path: string, options: RequestOptions = {}): Promise<T> {
    const { params, skipAuth = false, ...customConfig } = options;
    const url = this.buildUrl(path, params);

    const headers: Record<string, string> = {
      Accept: "application/json",
    };

    if (!(customConfig.body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    if (!skipAuth) {
      const token = this.getToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    if (customConfig.headers) {
      Object.assign(headers, customConfig.headers);
    }

    const config: RequestInit = {
      ...customConfig,
      headers,
    };

    let response = await fetch(url, config);

    // 401 Unauthorized -> Tentative de rafraichissement de token
    if (response.status === 401 && !skipAuth && this.getRefreshToken()) {
      if (!this.isRefreshing) {
        this.isRefreshing = true;
        const newToken = await this.tryRefreshToken();
        this.isRefreshing = false;

        if (newToken) {
          this.onTokenRefreshed(newToken);
          headers["Authorization"] = `Bearer ${newToken}`;
          response = await fetch(url, { ...config, headers });
        } else {
          // Token expire definitivement
          if (typeof window !== "undefined") {
            sessionStorage.removeItem("token");
            sessionStorage.removeItem("refresh_token");
            sessionStorage.removeItem("user");
            window.location.href = "/";
          }
        }
      } else {
        // En attente du refresh en cours
        const retryPromise = new Promise<T>((resolve, reject) => {
          this.addRefreshSubscriber(async (newToken: string) => {
            try {
              headers["Authorization"] = `Bearer ${newToken}`;
              const retryRes = await fetch(url, { ...config, headers });
              const retryData = await this.handleResponse<T>(retryRes);
              resolve(retryData);
            } catch (err) {
              reject(err);
            }
          });
        });
        return retryPromise;
      }
    }

    // 403 Forbidden -> Cle passerelle manquante (Clementino, Passenger, Yangzi)
    if (response.status === 403) {
      let errorBody: any = null;
      try {
        errorBody = await response.clone().json();
      } catch {}

      let targetApp = "PASSERELLE";
      if (path.includes("/clementino")) targetApp = "CLEMENTINO";
      else if (path.includes("/passenger")) targetApp = "PASSENGER";
      else if (path.includes("/yangzi")) targetApp = "YANGZI";

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("gateway_key_missing", {
            detail: {
              app: targetApp,
              message:
                errorBody?.message ||
                `Cle d'API ${targetApp} non configuree. L'administrateur ou vous-meme devez configurer cette cle pour acceder a ce service.`,
            },
          })
        );
      }
    }

    return this.handleResponse<T>(response);
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get("content-type");
    const isJson = contentType && contentType.includes("application/json");

    let responseData: any = null;
    if (isJson) {
      try {
        responseData = await response.json();
      } catch {
        responseData = null;
      }
    } else {
      try {
        responseData = await response.text();
      } catch {
        responseData = null;
      }
    }

    if (!response.ok) {
      let errorMessage = `Erreur ${response.status}: ${response.statusText}`;

      if (responseData && typeof responseData === "object") {
        if (responseData.message) {
          errorMessage = responseData.message;
        } else if (responseData.error) {
          errorMessage = typeof responseData.error === "string" ? responseData.error : JSON.stringify(responseData.error);
        } else if (responseData.errors) {
          if (Array.isArray(responseData.errors)) {
            errorMessage = responseData.errors.map((e: any) => e.errors || e.message || JSON.stringify(e)).join(", ");
          } else if (typeof responseData.errors === "string") {
            errorMessage = responseData.errors;
          }
        }
      }

      throw new ApiError(response.status, response.statusText, errorMessage, responseData);
    }

    return responseData as T;
  }

  public get<T = any>(path: string, params?: Record<string, any>, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: "GET", params });
  }

  public post<T = any>(path: string, body?: any, options?: RequestOptions): Promise<T> {
    const isFormData = body instanceof FormData;
    return this.request<T>(path, {
      ...options,
      method: "POST",
      body: isFormData ? body : JSON.stringify(body),
    });
  }

  public put<T = any>(path: string, body?: any, options?: RequestOptions): Promise<T> {
    const isFormData = body instanceof FormData;
    return this.request<T>(path, {
      ...options,
      method: "PUT",
      body: isFormData ? body : JSON.stringify(body),
    });
  }

  public patch<T = any>(path: string, body?: any, options?: RequestOptions): Promise<T> {
    const isFormData = body instanceof FormData;
    return this.request<T>(path, {
      ...options,
      method: "PATCH",
      body: isFormData ? body : JSON.stringify(body),
    });
  }

  public delete<T = any>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: "DELETE" });
  }
}

export const httpClient = new HttpClient();
